<template>
  <div class="flex items-end space-x-4" data-intercom-target="PageTitleLinks">
    <a
      v-if="tourIdSelected && mdBp && !isWhiteLabel"
      ref="vueTakeTourRef"
      href=""
      class="hover:text-neutral-70 focus:text-neutral-70 text-neutral-80 active:focus:text-neutral-70 inline-flex items-center gap-2 text-sm font-medium leading-5 transition-colors duration-150"
      data-intercom-target="PageTitleLink-TakeTour"
      @click.prevent="takeTour"
    >
      <AnnotationIcon class="-ml-0.5 h-4 w-4" aria-hidden="true" />
      <span v-if="lgBp" class="whitespace-nowrap">{{ t("takeTour") }}</span>
    </a>
    <Menu
      v-if="mdBp && !isWhiteLabel && helpMenuOptions.length > 0"
      v-slot="{ open }"
      as="div"
      class="relative inline-flex items-end"
    >
      <MenuButton
        ref="helpMenuButtonRef"
        as="a"
        class="hover:text-neutral-70 focus:text-neutral-70 text-neutral-80 active:focus:text-neutral-70 inline-flex items-center gap-2 text-sm font-medium leading-5 transition-colors duration-150 cursor-pointer focus:outline-none"
        data-intercom-target="PageTitleLink-HelpArticle"
      >
        <QuestionMarkCircleIcon class="-ml-0.5 h-4 w-4" aria-hidden="true" />
        <span v-if="lgBp" class="whitespace-nowrap">{{ t("helpCenter") }}</span>
      </MenuButton>
      <OpenStatus :open="open" @open="openHelpMenu" @close="closeHelpMenu" />
      <transition
        leave-active-class="transition duration-75 ease-out"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <MenuItems
          v-show="open"
          ref="helpMenuListRef"
          static
          class="absolute isolate z-50 rounded-md bg-white shadow-xl ring-1 ring-primary-40 ring-opacity-5 focus:outline-none"
        >
          <div class="py-1">
            <MenuItem v-for="item in helpMenuOptions" :key="item.key" v-slot="{ active }">
              <button
                :class="[active && 'bg-primary-99']"
                class="flex w-full items-center whitespace-nowrap px-4 py-2 text-left text-sm text-neutral-0 hover:bg-primary-99 focus:outline-none"
                @click="handleHelpMenuSelection(item)"
              >
                <component :is="item.icon" class="mr-2 h-5 w-5" aria-hidden="true" />
                <span class="truncate">{{ item.value }}</span>
              </button>
            </MenuItem>
          </div>
        </MenuItems>
      </transition>
    </Menu>
    <a
      v-if="videosArticleSelected && mdBp && !isWhiteLabel"
      ref="vueVideoTutorialsRef"
      href=""
      target="_blank"
      class="hover:text-neutral-70 focus:text-neutral-70 text-neutral-80 active:focus:text-neutral-70 inline-flex items-center gap-2 text-sm font-medium leading-5 transition-colors duration-150"
      data-intercom-target="PageTitleLink-VideoTutorial"
      @click.prevent="openVideosArticle"
    >
      <PlayIcon class="-ml-0.5 h-4 w-4" aria-hidden="true" />
      <span v-if="lgBp" class="whitespace-nowrap">{{ t("videoTutorials") }}</span>
    </a>
  </div>
</template>

<script setup lang="ts">
import { computed, markRaw, nextTick, ref, watchEffect } from "vue";

//Components
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import { OpenStatus } from "@helpers/headlessUI";
import type { DataItems, DataItem } from "@molecules/DropDownButton";

//Icons
import { AnnotationIcon, PlayIcon, QuestionMarkCircleIcon } from "@heroicons/vue/solid";
import { DocumentSearchIcon, ChatIcon } from "@heroicons/vue/outline";
import WhatsAppIcon from "@tokens/icons/WhatsAppIcon.vue";

//Popper
import { createPopper } from "@popperjs/core";

//Utils
import { useI18n } from "vue-i18n";
import { useBreakpoints } from "@composables/breakpoints";
import { localesLanguages } from "@locales/i18n";
import { useTooltip } from "@composables/tooltips";
import { openHelpArticle } from "@helpers/helpCenter";
import { openSupportChat } from "@helpers/supportChat";

//Types
export interface Link {
  helpId?: number;
  tourId?: number;
  videosArticleId?: number;
}

export type Links = Partial<Record<localesLanguages, Link>>;

const { lgBp, mdBp } = useBreakpoints();
const { t, locale } = useI18n();

//Props
const props = withDefaults(
  defineProps<{
    links: Links;
  }>(),
  {},
);

const emit = defineEmits<{
  (e: "tour"): void;
  (e: "help"): void;
  (e: "video"): void;
}>();

const vueTakeTourRef = ref();
const vueHelpCenterRef = ref();
const vueVideoTutorialsRef = ref();

const takeTourText = ref(t("takeTour"));
const helpCenterText = ref(t("helpCenter"));
const videoTutorialsText = ref(t("videoTutorials"));

const vueTakeTourTooltip = useTooltip(vueTakeTourRef, takeTourText, { placement: "bottom" });
const vueHelpCenterTooltip = useTooltip(vueHelpCenterRef, helpCenterText, { placement: "bottom" });
const vueVideoTutorialsTooltip = useTooltip(vueVideoTutorialsRef, videoTutorialsText, { placement: "bottom" });

watchEffect(() => {
  if (lgBp.value) {
    vueTakeTourTooltip.disable();
    vueHelpCenterTooltip.disable();
    vueVideoTutorialsTooltip.disable();
  } else {
    vueTakeTourTooltip.enable();
    vueHelpCenterTooltip.enable();
    vueVideoTutorialsTooltip.enable();
  }
});

//State
const windowConfig = computed(() => window.config);
const isWhiteLabel = computed(() => windowConfig.value?.whiteLabel ?? false);

const lang = computed(() => (locale.value?.slice(0, 2) as localesLanguages) || Object.keys(props.links)[0]);

const helpIdSelected = computed(() => (props.links[lang.value] ? props.links[lang.value]?.helpId : undefined));
const tourIdSelected = computed(() => (props.links[lang.value] ? props.links[lang.value]?.tourId : undefined));
const videosArticleSelected = computed(() =>
  props.links[lang.value] ? props.links[lang.value]?.videosArticleId : undefined,
);

const takeTour = () => {
  const element = document.getElementById("perfitPageUniqueID");

  if (element) {
    element.scrollTo({ top: 0, behavior: "smooth" });
  } else {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  emit("tour");
  window.Intercom("startTour", tourIdSelected.value);
};

const openArticle = () => {
  emit("help");
  if (helpIdSelected.value != null) {
    openHelpArticle(helpIdSelected.value);
  }
};
const openVideosArticle = () => {
  emit("video");
  if (videosArticleSelected.value != null) {
    openHelpArticle(videosArticleSelected.value);
  }
};

const WHATSAPP_URLS: Record<string, string> = {
  es: "https://wa.me/5492644662247",
  pt: "https://wa.me/551150286368",
};
const whatsAppUrl = computed(() => WHATSAPP_URLS[lang.value] ?? WHATSAPP_URLS.es);

enum HelpMenuKeys {
  helpCenter,
  talkToUs,
  whatsApp,
}

const helpMenuOptions = computed<DataItems>(() => {
  const items: DataItems = [];
  if (helpIdSelected.value != null) {
    items.push({
      key: HelpMenuKeys.helpCenter,
      icon: markRaw(DocumentSearchIcon),
      value: t("helpCenterLabel"),
    });
  }
  items.push(
    {
      key: HelpMenuKeys.talkToUs,
      icon: markRaw(ChatIcon),
      value: t("talkToUs"),
    },
    {
      key: HelpMenuKeys.whatsApp,
      icon: markRaw(WhatsAppIcon),
      value: t("whatsapp"),
    },
  );
  return items;
});

const helpMenuHandlers: Record<HelpMenuKeys, () => void> = {
  [HelpMenuKeys.helpCenter]: openArticle,
  [HelpMenuKeys.talkToUs]: () => {
    openSupportChat();
  },
  [HelpMenuKeys.whatsApp]: () => {
    window.open(whatsAppUrl.value, "_blank", "noopener,noreferrer");
  },
};

const handleHelpMenuSelection = (option: DataItem) => {
  helpMenuHandlers[option.key as HelpMenuKeys]?.();
};

const helpMenuButtonRef = ref();
const helpMenuListRef = ref();
let helpMenuPopper: ReturnType<typeof createPopper> | undefined;
let helpMenuTimeout: ReturnType<typeof setTimeout> | undefined;

const openHelpMenu = () => {
  if (helpMenuPopper) {
    clearTimeout(helpMenuTimeout);
    helpMenuTimeout = undefined;
    return;
  }

  helpMenuPopper = createPopper(helpMenuButtonRef.value?.el, helpMenuListRef.value?.el, {
    placement: "bottom-start",
    modifiers: [
      { name: "flip", options: { fallbackPlacements: ["bottom-start"], altBoundary: true } },
      { name: "offset", options: { offset: [0, 10] } },
    ],
  });
  nextTick(() => helpMenuPopper?.forceUpdate());
};

const closeHelpMenu = () => {
  helpMenuTimeout = setTimeout(() => {
    if (!helpMenuPopper) return;
    helpMenuPopper.destroy();
    helpMenuPopper = undefined;
  }, 100);
};
</script>

<i18n lang="jsonc">
{
  "es": {
    "helpCenter": "Ayuda",
    "helpCenterLabel": "Centro de ayuda",
    "talkToUs": "Hablá con nosotros",
    "whatsapp": "WhatsApp",
    "takeTour": "Tour",
    "videoTutorials": "Videos",
  },
  "pt": {
    "helpCenter": "Ajuda",
    "helpCenterLabel": "Central de ajuda",
    "talkToUs": "Fale conosco",
    "whatsapp": "WhatsApp",
    "takeTour": "Tour",
    "videoTutorials": "Videos",
  },
}
</i18n>
